import { createApi } from '@reduxjs/toolkit/query/react';

import {
  CreateDimensionInput,
  CreateDimensionItemInput,
  Dimension,
  DimensionItem,
  DimensionResponse,
  DimensionsResponse,
  UpdateDimensionInput,
} from '@/slices/dimensions/dimensions.types';

import { symfonyBaseQuery } from '@/services/SymfonyClient';

const dimensionsApi = createApi({
  baseQuery: symfonyBaseQuery({
    baseUrl: '/api/enterprises',
  }),
  reducerPath: 'dimensions',
  tagTypes: ['Dimensions'],
  endpoints: (build) => ({
    getDimensions: build.query<
      Dimension[],
      {
        enterpriseId: string;
      }
    >({
      query: ({ enterpriseId }) => ({
        url: `/${enterpriseId}/dimensions`,
        method: 'GET',
      }),
      transformResponse: (response: DimensionsResponse) =>
        response.dimensions ?? [],
      providesTags: (_result, _error, { enterpriseId }) => [
        { type: 'Dimensions', id: enterpriseId },
      ],
      async onQueryStarted({ enterpriseId }, { dispatch, queryFulfilled }) {
        try {
          const { data: dimensions } = await queryFulfilled;

          dispatch(
            dimensionsApi.util.upsertQueryEntries(
              dimensions.map((dimension) => ({
                endpointName: 'getDimension',
                arg: { enterpriseId, dimensionId: dimension.id },
                value: dimension,
              })),
            ),
          );
        } catch {
          // Keep cache untouched when fetching fails.
        }
      },
    }),
    getDimension: build.query<
      Dimension | null,
      {
        enterpriseId: string;
        dimensionId: string;
      }
    >({
      query: ({ enterpriseId, dimensionId }) => ({
        url: `/${enterpriseId}/dimensions/${dimensionId}`,
        method: 'GET',
      }),
      transformResponse: (response: DimensionResponse) => response.dimension,
      async onQueryStarted(
        { enterpriseId, dimensionId },
        { dispatch, queryFulfilled },
      ) {
        try {
          const { data: dimension } = await queryFulfilled;

          if (!dimension) {
            return;
          }

          dispatch(
            dimensionsApi.util.updateQueryData(
              'getDimensions',
              { enterpriseId },
              (draft) => {
                const index = draft.findIndex((d) => d.id === dimensionId);
                if (index >= 0) {
                  draft[index] = dimension;
                }
              },
            ),
          );
        } catch {
          // Keep cache untouched when fetching fails.
        }
      },
    }),
    createDimension: build.mutation<
      Dimension,
      {
        enterpriseId: string;
        input: CreateDimensionInput;
      }
    >({
      query: ({ enterpriseId, input }) => ({
        url: `/${enterpriseId}/dimensions`,
        method: 'POST',
        data: input,
      }),
      async onQueryStarted({ enterpriseId }, { dispatch, queryFulfilled }) {
        try {
          const { data: createdDimension } = await queryFulfilled;

          dispatch(
            dimensionsApi.util.updateQueryData(
              'getDimensions',
              { enterpriseId },
              (draft) => {
                draft.unshift(createdDimension);
              },
            ),
          );
        } catch {
          // Keep cache untouched when creation fails.
        }
      },
    }),
    createDimensionItems: build.mutation<
      DimensionItem[],
      {
        enterpriseId: string;
        dimensionId: string;
        input: {
          dimensionItems: CreateDimensionItemInput[];
        };
      }
    >({
      query: ({ enterpriseId, dimensionId, input }) => ({
        url: `/${enterpriseId}/dimensions/${dimensionId}/dimension-items`,
        method: 'POST',
        data: input,
      }),
      async onQueryStarted(
        { enterpriseId, dimensionId },
        { dispatch, queryFulfilled },
      ) {
        try {
          const { data: createdDimensionItems } = await queryFulfilled;

          dispatch(
            dimensionsApi.util.updateQueryData(
              'getDimensions',
              { enterpriseId },
              (draft) => {
                const dimension = draft.find((item) => item.id === dimensionId);
                if (!dimension) {
                  return;
                }

                dimension.dimensionItems.push(...createdDimensionItems);
              },
            ),
          );
        } catch {
          // Keep cache untouched when item creation fails.
        }
      },
    }),
    updateDimension: build.mutation<
      Dimension,
      {
        enterpriseId: string;
        dimensionId: string;
        input: UpdateDimensionInput;
      }
    >({
      query: ({ enterpriseId, dimensionId, input }) => ({
        url: `/${enterpriseId}/dimensions/${dimensionId}`,
        method: 'PATCH',
        data: input,
      }),
      async onQueryStarted(
        { enterpriseId, dimensionId },
        { dispatch, queryFulfilled },
      ) {
        try {
          const { data: updatedDimension } = await queryFulfilled;

          dispatch(
            dimensionsApi.util.updateQueryData(
              'getDimensions',
              { enterpriseId },
              (draft) => {
                const index = draft.findIndex(
                  (dimension) => dimension.id === dimensionId,
                );
                if (index >= 0) {
                  draft[index] = updatedDimension;
                }
              },
            ),
          );
        } catch {
          // Keep cache untouched when update fails.
        }
      },
    }),
    removeDimension: build.mutation<
      void,
      {
        enterpriseId: string;
        dimensionId: string;
      }
    >({
      query: ({ enterpriseId, dimensionId }) => ({
        url: `/${enterpriseId}/dimensions/${dimensionId}`,
        method: 'DELETE',
      }),
      async onQueryStarted(
        { enterpriseId, dimensionId },
        { dispatch, queryFulfilled },
      ) {
        const patchResult = dispatch(
          dimensionsApi.util.updateQueryData(
            'getDimensions',
            { enterpriseId },
            (draft) => {
              const index = draft.findIndex(
                (dimension) => dimension.id === dimensionId,
              );
              if (index >= 0) {
                draft.splice(index, 1);
              }
            },
          ),
        );

        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
  }),
});

export default dimensionsApi;
